'use client';

import { useRef, useState } from 'react';
import Link from 'next/link';
import { Check, Copy, Download, MessageCircle, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { trackGrowthEvent } from '@/lib/analytics';

interface PublicResultActionsProps {
    tool: string;
    title: string;
    value: string;
    description: string;
    sharePath: string;
}

export default function PublicResultActions({ tool, title, value, description, sharePath }: PublicResultActionsProps) {
    const cardRef = useRef<HTMLDivElement>(null);
    const [format, setFormat] = useState<'square' | 'story'>('square');
    const [copied, setCopied] = useState(false);
    const [busy, setBusy] = useState(false);

    const getShareUrl = () => `${window.location.origin}${sharePath}`;
    const shareText = `${title}: ${value}. ${description}`;

    const copyLink = async () => {
        trackGrowthEvent('share_opened', { type: tool, channel: 'copy' });
        try {
            await navigator.clipboard.writeText(`${shareText}\n\n${getShareUrl()}`);
            setCopied(true);
            trackGrowthEvent('share_completed', { type: tool, channel: 'copy' });
            window.setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error('Could not copy the result link');
        }
    };

    const nativeShare = async () => {
        trackGrowthEvent('share_opened', { type: tool, channel: 'native' });
        if (!navigator.share) {
            await copyLink();
            return;
        }
        try {
            await navigator.share({ title, text: shareText, url: getShareUrl() });
            trackGrowthEvent('share_completed', { type: tool, channel: 'native' });
        } catch (error) {
            if (!(error instanceof DOMException && error.name === 'AbortError')) toast.error('Could not open sharing');
        }
    };

    const downloadCard = async () => {
        if (!cardRef.current) return;
        setBusy(true);
        trackGrowthEvent('share_opened', { type: tool, channel: 'download', format });
        try {
            const { default: html2canvas } = await import('html2canvas');
            const canvas = await html2canvas(cardRef.current, { scale: 3, backgroundColor: '#171116', useCORS: true });
            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `youraura-${tool}-${format}.png`;
            link.click();
            trackGrowthEvent('share_completed', { type: tool, channel: 'download', format });
        } catch {
            toast.error('Could not create the share image');
        } finally {
            setBusy(false);
        }
    };

    const whatsappShare = () => {
        trackGrowthEvent('share_opened', { type: tool, channel: 'whatsapp' });
        trackGrowthEvent('share_completed', { type: tool, channel: 'whatsapp' });
        window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${getShareUrl()}`)}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="rounded-xl border border-[var(--card-border)] bg-[var(--card-surface-raised)] p-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                    <p className="text-sm font-bold text-foreground">Share this result</p>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">Preview contains only the result shown below, never your birth date, time, or place.</p>
                </div>
                <div className="inline-flex w-fit rounded-lg border border-[var(--card-border)] p-1" aria-label="Share image format">
                    {(['square', 'story'] as const).map((option) => (
                        <button key={option} type="button" onClick={() => setFormat(option)} aria-pressed={format === option} className={`rounded-md px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${format === option ? 'bg-accent text-[#1d1509]' : 'text-muted-foreground hover:text-foreground'}`}>
                            {option}
                        </button>
                    ))}
                </div>
            </div>

            <div ref={cardRef} className={`mx-auto mt-4 flex w-full max-w-sm flex-col justify-between overflow-hidden rounded-xl border border-accent/25 bg-[#171116] p-7 text-[#f7ead2] ${format === 'story' ? 'aspect-[9/16]' : 'aspect-square'}`}>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#d4af37]"><Share2 size={15} /> YourAura</div>
                <div>
                    <p className="text-sm text-[#ddceb0]/72">{title}</p>
                    <p className="mt-3 break-words font-playfair text-5xl font-bold leading-tight">{value}</p>
                    <p className="mt-4 text-sm leading-6 text-[#ddceb0]/78">{description}</p>
                </div>
                <p className="text-xs text-[#ddceb0]/55">Find your own result at youraura.life</p>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <button type="button" onClick={nativeShare} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[var(--card-border)] px-3 text-sm font-semibold text-foreground transition-colors hover:border-accent/40 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"><Share2 size={16} /> Share</button>
                <button type="button" onClick={copyLink} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[var(--card-border)] px-3 text-sm font-semibold text-foreground transition-colors hover:border-accent/40 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">{copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Copied' : 'Copy'}</button>
                <button type="button" onClick={whatsappShare} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[var(--card-border)] px-3 text-sm font-semibold text-foreground transition-colors hover:border-accent/40 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"><MessageCircle size={16} /> WhatsApp</button>
                <button type="button" onClick={downloadCard} disabled={busy} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[var(--card-border)] px-3 text-sm font-semibold text-foreground transition-colors hover:border-accent/40 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"><Download size={16} /> {busy ? 'Creating' : 'Download'}</button>
            </div>

            <Link href="/signup" onClick={() => trackGrowthEvent('result_to_signup', { tool, action: 'save_result' })} className="mt-4 inline-flex text-sm font-bold text-accent underline decoration-accent/35 underline-offset-4">
                Save this result and build my daily brief
            </Link>
        </div>
    );
}
